import { useEffect } from "react";

const NAV_SELECTOR = 'nav[class*="overflow-y-auto"], nav[class*="scrollbar"]';
const SETTLE_TIMEOUT_MS = 5000;
const USER_INTENT_EVENTS = [
  "wheel",
  "touchstart",
  "pointerdown",
  "keydown",
] as const;

function findActive(): HTMLAnchorElement | null {
  const all = Array.from(
    document.querySelectorAll<HTMLAnchorElement>(
      `${NAV_SELECTOR} a[aria-current="page"]`,
    ),
  );
  if (!all.length) return null;
  const currentPath = decodeURIComponent(location.pathname);
  return (
    all.find(
      (a) => decodeURIComponent(a.getAttribute("href") || "") === currentPath,
    ) ??
    all.reduce((best, a) =>
      (a.getAttribute("href") || "").length >
      (best.getAttribute("href") || "").length
        ? a
        : best,
    )
  );
}

function isInView(el: HTMLElement, scroller: HTMLElement) {
  const a = el.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  return a.top >= s.top && a.bottom <= s.bottom;
}

// 只滚 scroller 内部，不要用 scrollIntoView：后者会级联滚动所有祖先
// （包括 document），导致整页跳到 sidebar active 的对应位置——即用户反馈
// 的"先滚到底再跳转"。手动计算 scrollTop 即可把 active 居中到 scroller。
function centerActiveIn(scroller: HTMLElement, active: HTMLElement) {
  const a = active.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  const delta = a.top - s.top - s.height / 2 + a.height / 2;
  scroller.scrollTop += delta;
}

export function SidebarAutoScroll() {
  useEffect(() => {
    let cleanup: (() => void) | null = null;

    const engage = () => {
      cleanup?.();
      cleanup = null;

      const active = findActive();
      if (!active) return;
      const scroller = active.closest<HTMLElement>(NAV_SELECTOR);
      if (!scroller) return;

      centerActiveIn(scroller, active);

      let userInteracted = false;
      const stop = () => {
        scroller.removeEventListener("scroll", onScroll);
        USER_INTENT_EVENTS.forEach((e) =>
          scroller.removeEventListener(e, markUser),
        );
        clearTimeout(timer);
      };
      const markUser = () => {
        userInteracted = true;
        stop();
      };
      const onScroll = () => {
        if (userInteracted) return;
        if (!isInView(active, scroller)) {
          centerActiveIn(scroller, active);
        }
      };
      USER_INTENT_EVENTS.forEach((e) =>
        scroller.addEventListener(e, markUser, { once: true, passive: true }),
      );
      scroller.addEventListener("scroll", onScroll, { passive: true });
      const timer = setTimeout(stop, SETTLE_TIMEOUT_MS);

      cleanup = stop;
    };

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        engage();
      });
    };

    schedule();

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-current"],
    });

    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
      cleanup?.();
    };
  }, []);

  return null;
}
